import { useEffect, useMemo, useState } from 'react';
import { Calendar, Filter, Plus, RefreshCw, Search, X } from 'lucide-react';
import { api } from '../api/client';
import ArticleCard from '../components/ArticleCard';
import ArticleDetailModal from '../components/ArticleDetailModal';
import CollectionDrawer from '../components/CollectionDrawer';

function defaultFromDate() {
  const date = new Date();
  date.setDate(date.getDate() - 3);
  return date.toISOString().slice(0, 10);
}

export default function DashboardPage() {
  const [filters, setFilters] = useState({
    from_date: defaultFromDate(),
    region: 'all',
    status: 'new',
    search: '',
    page: '1',
    per_page: '24',
  });
  const [articles, setArticles] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [selected, setSelected] = useState({});
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const selectedArticles = useMemo(() => Object.values(selected), [selected]);
  const regions = useMemo(() => {
    const values = new Set();
    sources.forEach((source) => {
      (source.category || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .forEach((item) => values.add(item));
    });
    return [...values].sort((a, b) => a.localeCompare(b, 'de'));
  }, [sources]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [articleData, statsData, sourcesData] = await Promise.all([
        api.articles(filters),
        api.stats(),
        api.sources(),
      ]);
      setArticles(articleData.articles);
      setPagination(articleData.pagination);
      setStats(statsData);
      setSources(sourcesData.sources || sourcesData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [filters.page, filters.status, filters.region, filters.from_date]);

  function updateFilter(key, value) {
    setFilters((current) => ({ ...current, [key]: value, page: '1' }));
  }

  async function search(event) {
    event.preventDefault();
    await load();
  }

  async function openArticle(id) {
    setDetailLoading(true);
    setDetail(null);
    try {
      const data = await api.article(id);
      setDetail(data.article);
    } finally {
      setDetailLoading(false);
    }
  }

  async function setStatus(id, status) {
    const data = await api.setArticleStatus(id, status);
    const next = data.article;
    setArticles((current) => current.map((article) => (article.id === id ? next : article)));
    if (status === 'selected') {
      setSelected((current) => ({ ...current, [id]: next }));
    } else {
      setSelected((current) => {
        const copy = { ...current };
        delete copy[id];
        return copy;
      });
    }
    if (detail?.id === id) setDetail(next);
  }

  async function toggleSelected(article) {
    if (selected[article.id] || article.status === 'selected') {
      await setStatus(article.id, 'new');
    } else {
      await setStatus(article.id, 'selected');
    }
  }

  async function bulkReject() {
    const ids = selectedArticles.map((article) => article.id);
    if (!ids.length) return;
    await api.bulkStatus(ids, 'rejected');
    setSelected({});
    await load();
  }

  async function createCollection(payload) {
    await api.createCollection(payload);
    setSelected({});
    setDrawerOpen(false);
    await load();
  }

  async function extractArticle(articleId, payload) {
    const data = await api.extractArticle(articleId, payload);
    const selectedData = await api.setArticleStatus(data.article.id, 'selected');
    const extractedArticle = selectedData.article;
    setSelected((current) => ({ ...current, [extractedArticle.id]: extractedArticle }));
    await load();
    return extractedArticle;
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Phase 1</span>
          <h1>Redaktions-Dashboard</h1>
        </div>
        <button className="secondary-button" onClick={load} type="button">
          <RefreshCw size={16} />
          Refresh
        </button>
      </header>

      <section className="metrics">
        <div>
          <strong>{pagination.total}</strong>
          <span>Artikel im Filter</span>
        </div>
        <div>
          <strong>{selectedArticles.length}</strong>
          <span>Selected</span>
        </div>
        <div>
          <strong>{stats?.active_sources ?? '-'}</strong>
          <span>Aktive Quellen</span>
        </div>
        <div>
          <strong>{stats?.new_articles ?? '-'}</strong>
          <span>Neu gesamt</span>
        </div>
      </section>

      <form className="filter-bar" onSubmit={search}>
        <label>
          <Calendar size={16} />
          <input value={filters.from_date} onChange={(event) => updateFilter('from_date', event.target.value)} type="date" />
        </label>
        <label>
          <Filter size={16} />
          <select value={filters.region} onChange={(event) => updateFilter('region', event.target.value)}>
            <option value="all">Alle Regionen</option>
            {regions.map((region) => (
              <option key={region} value={region}>
                {region}
              </option>
            ))}
          </select>
        </label>
        <select value={filters.status} onChange={(event) => updateFilter('status', event.target.value)}>
          <option value="new">New</option>
          <option value="selected">Selected</option>
          <option value="reviewed">Reviewed</option>
          <option value="rejected">Rejected</option>
        </select>
        <label className="search-field">
          <Search size={16} />
          <input
            value={filters.search}
            onChange={(event) => setFilters((current) => ({ ...current, search: event.target.value }))}
            placeholder="Suche"
          />
        </label>
        <button className="secondary-button" type="submit">
          Search
        </button>
        <button
          className="secondary-button"
          onClick={() => setFilters({ from_date: defaultFromDate(), region: 'all', status: 'new', search: '', page: '1', per_page: '24' })}
          type="button"
        >
          <X size={16} />
          Clear
        </button>
      </form>

      <div className="selection-bar">
        <span>{selectedArticles.length} Artikel ausgewahlt</span>
        <div>
          <button className="secondary-button" disabled={!selectedArticles.length} onClick={bulkReject} type="button">
            Reject Selected
          </button>
          <button className="primary-button" disabled={!selectedArticles.length} onClick={() => setDrawerOpen(true)} type="button">
            <Plus size={16} />
            Create Collection
          </button>
        </div>
      </div>

      {error ? <div className="alert">{error}</div> : null}
      {loading ? <div className="screen-status">Artikel laden...</div> : null}

      <section className="article-grid">
        {articles.map((article) => (
          <ArticleCard
            key={article.id}
            article={article}
            checked={Boolean(selected[article.id])}
            onToggle={() => toggleSelected(article)}
            onStatus={(status) => setStatus(article.id, status)}
            onOpen={() => openArticle(article.id)}
          />
        ))}
      </section>

      <footer className="pagination">
        <button
          className="secondary-button"
          disabled={pagination.page <= 1}
          onClick={() => setFilters((current) => ({ ...current, page: String(Number(current.page) - 1) }))}
          type="button"
        >
          Previous
        </button>
        <span>
          Seite {pagination.page} von {pagination.pages || 1}
        </span>
        <button
          className="secondary-button"
          disabled={pagination.page >= pagination.pages}
          onClick={() => setFilters((current) => ({ ...current, page: String(Number(current.page) + 1) }))}
          type="button"
        >
          Next
        </button>
      </footer>

      <ArticleDetailModal
        article={detail}
        loading={detailLoading}
        onClose={() => setDetail(null)}
        onExtract={extractArticle}
        onOpenCollection={() => {
          setDetail(null);
          setDrawerOpen(true);
        }}
        onStatus={setStatus}
        selectedArticles={selectedArticles}
      />
      {drawerOpen ? <CollectionDrawer articles={selectedArticles} onClose={() => setDrawerOpen(false)} onCreate={createCollection} /> : null}
    </div>
  );
}
